import {
  getStoreContext,
  sha256Hash,
  storePaymentIntent,
} from '../../../utils';
import { action, makeObservable, observable, runInAction } from 'mobx';
import {
  requestDeviceAvailability,
  requestPaymentIntent,
  requestQrToken,
} from '../services';
import { FAIR_MODE_CLIENT_SECRET, UserInfo } from '@web/common';

class OrderDataStore {
  //orderData is only used to save form data when switching between steps, making it observable leads to endless function calls when navigating back
  public orderData: UserInfo | undefined = undefined;

  @observable public qrToken: string | undefined = undefined;
  @observable public clientSecret: string | undefined = undefined;
  @observable public clientSecretLoading = false;
  @observable public qrTokenLoading = false;
  @observable public clientSecretLoadingError = false;
  @observable public deviceAvailable = false;
  @observable public deviceFairMode = false;
  @observable public checkingDeviceAvailability = false;
  @observable public errorCheckingDeviceAvailability = false;

  constructor() {
    makeObservable(this);
  }

  @action public setOrderData(orderData: UserInfo) {
    this.orderData = orderData;
  }

  @action public async loadClientSecret(authToken: string) {
    if (this.orderData && !this.clientSecretLoading) {
      this.clientSecretLoadingError = false;
      this.clientSecretLoading = true;
      try {
        const deviceId = localStorage.getItem('device_id');
        const response = await requestPaymentIntent(
          this.orderData,
          authToken,
          deviceId || undefined,
        );

        runInAction(() => {
          this.clientSecret = response.client_secret || undefined;
          this.deviceFairMode =
            response.client_secret === FAIR_MODE_CLIENT_SECRET;
          if (response.id && response.client_secret) {
            storePaymentIntent(response.id, response.client_secret);
          }
        });
      } catch {
        runInAction(() => {
          this.clientSecretLoadingError = true;
        });
        console.log('error while loading client secret');
      } finally {
        runInAction(() => {
          this.clientSecretLoading = false;
        });
      }
    }
  }

  @action public async loadQrToken() {
    if (!this.qrTokenLoading) {
      this.qrTokenLoading = true;
      const paymentId = JSON.parse(
        localStorage.getItem('payment_intent') || '',
      );
      const client_secret = JSON.parse(
        localStorage.getItem('payment_intent_client_secret') || '',
      );

      const hash = await sha256Hash(client_secret);
      try {
        const token = await requestQrToken(paymentId, hash);
        runInAction(() => {
          this.qrToken = token;
        });
      } catch {
        console.log('Error while loading qr code token');
        runInAction(() => {
          this.qrToken = 'error';
        });
      } finally {
        runInAction(() => {
          this.qrTokenLoading = false;
        });
      }
    }
  }

  @action public async checkDeviceAvailability(deviceId: string) {
    if (!this.checkingDeviceAvailability) {
      this.checkingDeviceAvailability = true;
      const paymentId = JSON.parse(
        localStorage.getItem('payment_intent') || '',
      );
      const client_secret = JSON.parse(
        localStorage.getItem('payment_intent_client_secret') || '',
      );

      const hash = await sha256Hash(client_secret);
      try {
        const response = await requestDeviceAvailability(
          paymentId,
          deviceId,
          hash,
        );
        runInAction(() => {
          this.deviceAvailable = response.isAvailable;
          this.errorCheckingDeviceAvailability = false;
        });
      } catch (error) {
        console.log('Error while checking device availability', error);
        runInAction(() => {
          this.deviceAvailable = false;
          this.errorCheckingDeviceAvailability = true;
        });
      } finally {
        runInAction(() => {
          this.checkingDeviceAvailability = false;
        });
      }
    }
  }
}

const orderDataStore = new OrderDataStore();
export const {
  Context: OrderDataStoreContext,
  Provider: OrderDataStoreProvider,
} = getStoreContext<OrderDataStore>(orderDataStore);
export default orderDataStore;
